import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

// --- IMAGE IMPORTS ---
import bannerImg from '../assets/images/banner.png';
import lauraSteele from '../assets/team/laura-steele.jpg'; // AUTHOR IMAGE

// --- BLOG IMAGE IMPORTS (FROM ASSETS/IMAGES) ---
import securityServicesImg from '../assets/images/security-services.png';
import sectorEventsImg from '../assets/images/sector-events.jpg';
import receptionHeroImg from '../assets/images/reception-security-hero.png';
import monitorAdaptImg from '../assets/images/monitor and adapt.jpg';
import consultAccessImg from '../assets/images/consult & access.jpg';
import designDeployImg from '../assets/images/design & deploy.jpg';
import staticGuardingImg from '../assets/images/static guarding.png';


// --- ENTERPRISE ARTICLE DATA ---
const articleData = {
  'security-more-than-guards-and-gates': {
    title: "Security Is More Than Guards & Gates",
    category: "Strategic Insight",
    date: "14 Mar 2026",
    readTime: "5 Min Read",
    author: "Laura Steele",
    authorRole: "Group Business Director",
    image: securityServicesImg,
    lead: "Modern security sits at the intersection of people, operations, risk management and organisational resilience. The best providers protect operations, not just buildings.",
    content: (
      <>
        <p>For many organisations, security is still viewed as a contracted service. A requirement. A cost. A team of officers positioned at entrances, carrying out patrols and responding when incidents occur.</p>
        <p>The reality is that modern security has evolved far beyond that traditional model. Today, security sits at the intersection of people, operations, risk management and organisational resilience. The best security providers do not simply protect buildings. They protect operations.</p>
        <p>Every day, security teams are involved in decisions and activities that directly impact business continuity, public safety, customer experience, compliance and organisational reputation.</p>
        
        <ul className="custom-bullet-list">
          <li>They are often the first people responding when something goes wrong.</li>
          <li>The first people managing an emergency.</li>
          <li>The first people supporting vulnerable individuals.</li>
          <li>The first people coordinating information during a developing incident.</li>
        </ul>

        <h2>A Strategic Evolution</h2>
        <p>Increasingly, security teams are expected to provide far more than a physical presence. Following the introduction of Martyn's Law, organisations are facing greater expectations around preparedness, communication, public protection and operational resilience than ever before.</p>
        <p>At the same time, organisations are operating within increasingly complex environments where risks evolve quickly and disruption can have immediate operational consequences. As a result, security is becoming a strategic function rather than simply an operational one. The conversation is no longer: <em>"How many officers do we need?"</em> It is: <em>"How do we protect our people, maintain continuity and reduce risk?"</em></p>
        
        <h2>The Modern Approach</h2>
        <p>This requires a very different approach. Modern security should combine frontline operational capability, intelligence-led decision making, monitoring and escalation support, business continuity planning, public protection preparedness, technology integration, and consultancy expertise.</p>
        <p>At Nightmark Group, this is exactly how we operate. We believe security should function as an extension of our partners' operations rather than a standalone service. Through our integrated approach, we support organisations not only with protecting people and assets, but also with understanding risk, strengthening resilience and preparing for the challenges they may face tomorrow.</p>

        <div className="article-blockquote">
          <p>"Ultimately, modern security is not about guarding doors. It is about protecting organisations."</p>
        </div>

        <p>Whether supporting student accommodation providers, healthcare environments, local authorities, hospitality venues, logistics operators or public-facing estates, our objective remains the same: To provide organisations with confidence. Confidence that incidents will be managed effectively. Confidence that risks are understood. Confidence that disruption can be minimised. And confidence that there is a capable partner standing alongside them when it matters most.</p>
      </>
    )
  },
  'why-diverse-workforce-makes-security-stronger': {
    title: "Why a Diverse Workforce Makes Security Stronger",
    category: "Corporate Culture",
    date: "10 Mar 2026",
    readTime: "4 Min Read",
    author: "Laura Steele",
    authorRole: "Group Business Director",
    image: sectorEventsImg,
    lead: "Security is ultimately about people. No matter how advanced technology becomes, effective security still relies on communication, judgement, cultural awareness, and emotional intelligence.",
    content: (
      <>
        <p>Security is ultimately about people. No matter how advanced technology becomes, effective security still relies on communication, judgement, cultural awareness, emotional intelligence and the ability to build trust within the environments being protected.</p>
        <p>That is why diversity within a workforce is not simply a corporate initiative — it is a genuine operational advantage. At Nightmark Group, we believe diverse teams create stronger operational outcomes.</p>
        
        <h2>Navigating Multicultural Environments</h2>
        <p>Modern security environments are increasingly complex and multicultural. Whether supporting student accommodation, hospitality venues, logistics operations, public-facing estates or corporate environments, security teams are often engaging with people from vastly different backgrounds, cultures, languages and lived experiences every single day.</p>
        
        <p>A workforce that reflects those environments is naturally better positioned to:</p>
        <ul className="custom-bullet-list">
          <li>Communicate effectively and build rapport.</li>
          <li>Understand cultural sensitivities.</li>
          <li>De-escalate situations smoothly.</li>
          <li>Create safer, more welcoming environments for clients, residents, visitors and staff.</li>
        </ul>

        <h2>Global Experience Driving Mindset</h2>
        <p>This philosophy has been heavily influenced by the experience and perspective brought into Nightmark Group by investor Mehtab Qureshi, whose business experience spans multiple sectors and international markets across different regions of the world.</p>
        <p>That global business exposure brings significant value to the group’s long-term direction and mindset. Operating across international markets provides a deep understanding of how organisations, cultures and people work differently - and why adaptability, communication and cultural intelligence are increasingly important within modern businesses.</p>
        
        <div className="article-blockquote">
          <p>"For Nightmark Group, diversity is not simply about representation on paper. It is about building teams capable of operating effectively within real-world environments."</p>
        </div>

        <p>In practice, this means creating opportunities across different backgrounds and communities, supporting veterans and individuals transitioning into civilian careers, giving people genuine opportunities to develop within the industry, and ensuring recruitment focuses on attitude, professionalism and operational capability rather than narrow traditional pathways alone.</p>
        <p>The business also recognises that strong operational teams require different strengths. Some individuals thrive within customer engagement, others within operational control, technology, intelligence, compliance or incident response. A modern security business benefits from bringing together different experiences, viewpoints and skill sets into one cohesive operational culture.</p>
        
        <h2>Strengthening Decision Making</h2>
        <p>Importantly, diversity also strengthens decision making. Businesses made up of people with varied experiences, perspectives and backgrounds are often better equipped to challenge assumptions, identify operational blind spots, adapt to changing environments, and innovate more effectively.</p>
        <p>This becomes particularly important within sectors such as student accommodation, public-facing estates, hospitality, higher education, and cultural venues, where customer experience and community engagement are just as important as operational security itself.</p>
        <p>At Nightmark Group, the long-term vision is to continue building a workforce and leadership structure that reflects the environments and communities we support - combining operational professionalism with cultural awareness, adaptability and partnership-led thinking. Because ultimately, stronger teams create stronger security.</p>
      </>
    )
  },
  'why-diversity-strengthens-security': {
    title: "Why Diversity Strengthens Security",
    category: "Corporate Culture",
    date: "05 Mar 2026",
    readTime: "4 Min Read",
    author: "Laura Steele",
    authorRole: "Group Business Director",
    image: receptionHeroImg,
    lead: "At Nightmark Group, diversity is embedded throughout our business, from frontline operations through to ownership, executive leadership and board representation.",
    content: (
      <>
        <p>At Nightmark Group, diversity is not something we talk about because we feel we should. It is something that is embedded throughout our business, from frontline operations through to ownership, executive leadership and board representation.</p>
        <p>Our board and senior leadership team bring together a broad range of cultural backgrounds, experiences and perspectives, alongside strong female representation at senior level. We believe this diversity of thought is one of the reasons we continue to challenge traditional thinking within the security industry and develop solutions that are genuinely aligned to the needs of modern organisations.</p>
        
        <div className="article-blockquote">
          <p>"For us, diversity is not simply about representation. It is about perspective."</p>
        </div>

        <h2>Reflecting the Communities We Serve</h2>
        <p>The environments we support are increasingly diverse and multicultural. Whether working within student accommodation, healthcare, hospitality, local authorities, public venues or commercial estates, our teams engage daily with people from different backgrounds, cultures, faiths and life experiences.</p>
        <p>To effectively protect diverse communities, organisations must first understand them. A leadership team that reflects different cultures, experiences and viewpoints is better positioned to understand evolving societal and operational challenges, make balanced and informed decisions, challenge traditional assumptions, drive innovation, and build more inclusive working environments.</p>

        <h2>Driving Operational Excellence</h2>
        <p>This philosophy extends throughout our business. We actively promote equality, diversity and inclusion through recruitment, development and progression opportunities, ensuring individuals are recognised for their ability, commitment and contribution rather than their background.</p>
        <p>Importantly, we believe diverse teams deliver stronger operational outcomes. Different experiences lead to different perspectives, which in turn strengthen communication, problem-solving, conflict resolution, cultural awareness, customer engagement, and operational decision-making.</p>
        <p>In an industry where trust, communication and understanding people are fundamental to success, these qualities matter.</p>
        <p>At Nightmark Group, we do not view diversity as a compliance requirement or a box-ticking exercise. We view it as a business strength. Because stronger representation creates broader perspectives, broader perspectives create better decisions, and better decisions ultimately create safer environments for the people and communities we serve.</p>
      </>
    )
  },
  'why-operational-intelligence-matters': {
    title: "Why Operational Intelligence Matters",
    category: "Operational Excellence",
    date: "28 Feb 2026",
    readTime: "5 Min Read",
    author: "Laura Steele",
    authorRole: "Group Business Director",
    image: monitorAdaptImg,
    lead: "Operational intelligence is the process of transforming data into informed decision making, allowing organisations to move beyond reacting to incidents.",
    content: (
      <>
        <p>Most organisations collect vast amounts of operational data every day. Incident reports are submitted, patrols are completed, CCTV systems generate alerts, contractors record issues, customer complaints are logged and security teams respond to operational challenges across multiple sites.</p>
        <p>Yet in many organisations, this information remains isolated within individual reports, spreadsheets and systems, never being translated into meaningful operational insight.</p>
        
        <h2>Transforming Data into Decision Making</h2>
        <p>The challenge is not a lack of information. The challenge is understanding what that information is actually telling you. Operational intelligence is the process of transforming data into informed decision making. It allows organisations to move beyond reacting to incidents and instead understand the trends, behaviours and vulnerabilities driving them.</p>
        <p>For example, a single access control failure may be an isolated incident. Ten access control failures occurring across multiple locations over a three-month period may indicate a systemic weakness requiring investment, training or procedural change. Likewise, an increase in anti-social behaviour, contractor breaches or welfare incidents may reveal emerging risks long before they become significant operational problems.</p>

        <div className="article-blockquote">
          <p>"The organisations that perform best operationally are rarely those with the most data. They are the organisations that use information effectively to make decisions."</p>
        </div>

        <h2>Answering Critical Operational Questions</h2>
        <p>Operational intelligence enables organisations to answer critical questions such as:</p>
        <ul className="custom-bullet-list">
          <li>Where are our greatest operational risks?</li>
          <li>Which sites require additional resource?</li>
          <li>What incidents are increasing and why?</li>
          <li>Are our current control measures working?</li>
          <li>Where should investment be focused?</li>
          <li>What emerging trends could affect our operation in the future?</li>
        </ul>

        <p>This becomes increasingly important within complex environments such as student accommodation, healthcare, local authorities, logistics operations, hospitality venues and large multi-site estates where operational risk is constantly evolving.</p>
        <p>At Nightmark Group, we view operational intelligence as far more than a security reporting function. It is a business tool that supports operational resilience, resource planning, public protection, business continuity and strategic decision making.</p>
        <p>Through intelligence-led reporting, trend analysis, operational reviews and real-time monitoring, we help organisations move from simply recording events to understanding them. Because information only becomes valuable when it helps organisations make better decisions tomorrow than they made yesterday.</p>
      </>
    )
  },
  'vested-partnerships-future-security-fm': {
    title: "Why Vested Partnerships Are Changing the Future of Security & FM",
    category: "Strategic Insight",
    date: "20 Feb 2026",
    readTime: "6 Min Read",
    author: "Laura Steele",
    authorRole: "Group Business Director",
    image: consultAccessImg,
    lead: "A vested partnership moves away from the traditional transactional supplier relationship and instead creates a shared operational objective between both parties.",
    content: (
      <>
        <p>For years, many service contracts within security, facilities management and operational outsourcing have been built around a traditional buyer-versus-supplier mentality. Procurement teams drive costs down, providers compete aggressively on price and contracts become heavily focused around KPIs, penalties and minimum service obligations.</p>
        <p>The problem is that this model often creates competing objectives.</p>
        
        <h2>The Conflict of Traditional Contracts</h2>
        <p>The client wants: innovation, resilience, proactive support, operational flexibility, and long-term value. The provider, meanwhile, is often forced into: protecting shrinking margins, limiting scope creep, reducing overhead, and reacting to issues rather than solving them strategically.</p>
        <p>This is where vested partnerships are becoming increasingly important. A vested partnership moves away from the traditional transactional supplier relationship and instead creates a shared operational objective between both parties. Rather than simply "buying hours" or "managing a contract," both organisations work collaboratively towards common long-term outcomes.</p>

        <div className="article-blockquote">
          <p>"In simple terms: the client succeeds, the provider succeeds, and both parties become aligned operationally rather than commercially opposed."</p>
        </div>

        <h2>Creating Genuine Value</h2>
        <p>Within security and FM environments, this can completely change the quality of service delivery. The most effective security operations are rarely achieved through rigid contracts alone. They are achieved when providers genuinely understand the client’s operational pressures, risk profile, customer expectations and business objectives — and are trusted to help shape solutions collaboratively.</p>
        
        <p>A true partnership approach creates an environment where providers can:</p>
        <ul className="custom-bullet-list">
          <li>Proactively identify risk and recommend operational improvements.</li>
          <li>Invest in innovation and support long-term resilience.</li>
          <li>Challenge inefficient processes constructively.</li>
        </ul>
        
        <p>Instead of simply waiting for instructions, the provider becomes an extension of the organisation itself. This is particularly important within modern public-facing environments where operational pressures constantly evolve (public protection, Martyn’s Law preparedness, workforce challenges, compliance, and resilience planning). Organisations increasingly need partners capable of supporting much more than frontline staffing alone.</p>

        <h2>Transparency & Accountability</h2>
        <p>A vested partnership also creates stronger operational transparency. Rather than hiding problems behind contractual boundaries, both parties are incentivised to solve issues collaboratively. This leads to stronger communication, better workforce stability, improved mobilisation success, faster decision making, and significantly better long-term service quality.</p>
        <p>Lowest-cost procurement models often result in excessive staff turnover, reduced management support, inconsistent service delivery, and providers operating reactively. By contrast, partnership-led contracts allow providers to invest properly into training, technology, intelligence, and customer experience. The result is lower long-term operational risk and stronger overall performance.</p>
        <p>Importantly, vested partnerships do not remove accountability - they improve it. Strong governance and transparent reporting remain essential. However, the relationship becomes focused around continuous improvement.</p>
        <p>At Nightmark Group, we strongly believe the future of security and operational support sits within partnership-led delivery models. Our focus is always on becoming an operational extension of the organisations we support - aligning objectives, strengthening resilience and creating long-term value.</p>
      </>
    )
  },
  'how-security-supports-business-continuity': {
    title: "How Security Supports Business Continuity",
    category: "Risk Management",
    date: "12 Feb 2026",
    readTime: "5 Min Read",
    author: "Laura Steele",
    authorRole: "Group Business Director",
    image: designDeployImg,
    lead: "Effective security operations are often one of the first and most important layers of business continuity during disruption, incidents and crisis situations.",
    content: (
      <>
        <p>When organisations think about business continuity, the conversation often focuses on IT systems, cyber resilience or disaster recovery plans. While these are critical, one area that is still regularly underestimated is the role security plays in maintaining operational continuity during disruption, incidents and crisis situations.</p>
        <p>In reality, effective security operations are often one of the first and most important layers of business continuity.</p>
        
        <h2>Managing Disruption in Real Time</h2>
        <p>Whether the disruption involves a major incident, fire alarm activation, protest activity, loss of access control, power failure, suspicious package, critical infrastructure failure, workforce disruption, or a public protection incident, security teams are usually the people managing the operational reality in real time.</p>
        <p>Strong security provision does far more than simply protect buildings. It supports operational control, communication, escalation management, incident coordination, workforce safety, visitor reassurance, and decision making during periods of disruption.</p>

        <div className="article-blockquote">
          <p>"The difference between disruption being contained quickly or escalating operationally often comes down to preparedness, communication and the quality of the security response structure already in place."</p>
        </div>

        <h2>Integrating Security and Continuity</h2>
        <p>One of the biggest mistakes organisations make is treating security and business continuity as completely separate functions. In reality, they should operate together.</p>
        <p>A modern security provider should be capable of supporting contingency planning, escalation procedures, emergency communication structures, control room resilience, backup monitoring, incident response coordination, and operational recovery planning. This becomes particularly important within public-facing estates, student accommodation, hospitality environments, logistics operations, and healthcare.</p>

        <h2>Control Room Capability & Preparedness</h2>
        <p>Control room capability is a major part of this. A resilient monitoring structure can provide organisations with real-time operational visibility, escalation management, incident logging, communication support, lone worker monitoring, and operational coordination during major incidents.</p>
        <p>At Nightmark Group, business continuity is treated as an operational function rather than simply a written document. Through our wider group capability — including monitoring, operational support, intelligence-led reporting and consultancy — we support organisations in building practical resilience structures that work operationally during real incidents.</p>
        <p>Unlike many traditional security providers, Nightmark Group also supports clients through dedicated Business Continuity Planning (BCP) and operational resilience capability. This includes contingency planning, tabletop exercises, operational reviews, escalation testing, and vulnerability assessments.</p>
        <p>Increasingly, organisations are also recognising the link between business continuity and public protection responsibilities following the introduction of Martyn’s Law. The organisations that recover fastest from disruption are usually not the ones with the largest written plans — they are the ones with the clearest operational structures, strongest communication pathways and best-prepared teams. Security sits at the centre of that.</p>
      </>
    )
  },
  'how-threat-intelligence-supports-public-safety': {
    title: "How Threat Intelligence Supports Public Safety",
    category: "Sector Focus",
    date: "05 Feb 2026",
    readTime: "4 Min Read",
    author: "Laura Steele",
    authorRole: "Group Business Director",
    image: staticGuardingImg,
    lead: "Public safety threats rarely emerge without warning signs. Threat intelligence helps organisations respond more effectively by identifying risks early.",
    content: (
      <>
        <p>Public safety threats rarely emerge without warning signs. Operational disruption, protest activity, disorder, hostile behaviour, suspicious activity and wider public protection concerns are often preceded by patterns, indicators or intelligence that — if identified early — can help organisations respond more effectively.</p>
        <p>That is why threat intelligence is becoming increasingly important within modern security and public protection environments.</p>

        <h2>Awareness Over Fear</h2>
        <p>Threat intelligence involves gathering, assessing and interpreting information that may indicate emerging operational or security risks. The purpose is not to create fear — it is to improve awareness, preparedness and decision making.</p>
        
        <p>Within public-facing environments, this can support:</p>
        <ul className="custom-bullet-list">
          <li>Operational planning and resource deployment.</li>
          <li>Event preparedness and escalation management.</li>
          <li>Public reassurance and incident prevention.</li>
        </ul>

        <div className="article-blockquote">
          <p>"Threat intelligence can come from multiple sources including operational reporting, open-source intelligence, local incident trends, behavioural observations, and event intelligence."</p>
        </div>

        <h2>Turning Information into Action</h2>
        <p>Importantly, intelligence only becomes valuable if it supports operational action. At Nightmark Group, our approach focuses heavily on turning operational information into practical decision-making support. This includes intelligence-led reporting, operational briefings, escalation support, real-time situational awareness, and proactive risk identification.</p>
        <p>This becomes particularly important within public venues, student accommodation, hospitality, entertainment, logistics, and high-footfall environments where operational risks can evolve rapidly.</p>
        <p>Increasingly, organisations also need stronger threat awareness as public protection expectations evolve under Martyn’s Law and wider operational resilience frameworks. Because ultimately, public safety is strengthened when organisations are able to identify and understand risk before incidents escalate operationally.</p>
      </>
    )
  }
};

const NewsDetail = () => {
  const { slug } = useParams();
  
  // If the slug from the URL matches a blog in our object, use it. 
  // Otherwise, default to the featured post to prevent crashing.
  const article = articleData[slug] || articleData['security-more-than-guards-and-gates'];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]); // Re-run scroll when slug changes

  return (
    <div className="news-detail-page bg-white">
      
      {/* INJECTED STYLES FOR EDITORIAL ARTICLE UI */}
      <style>
        {`
          /* --- HIDE GLOBAL HERO ARROW --- */
          .news-hero::after,
          .news-detail-hero::after {
            display: none !important;
          }

          .article-hero-image {
            width: 100%;
            height: 60vh;
            min-height: 400px;
            background-size: cover;
            background-position: center;
            border-radius: 16px;
            box-shadow: 0 25px 50px rgba(0,0,0,0.2);
            position: relative;
            z-index: 10;
            margin-top: -6rem; 
            margin-bottom: 5rem;
          }

          .article-layout-grid {
            display: grid;
            grid-template-columns: 3fr 8fr;
            gap: 6rem;
            padding-bottom: 6rem;
          }

          .article-sidebar {
            position: sticky;
            top: 120px;
            align-self: start;
          }

          .author-block {
            display: flex;
            align-items: center;
            gap: 1.5rem;
            padding-bottom: 2rem;
            border-bottom: 1px solid rgba(0,0,0,0.1);
            margin-bottom: 2rem;
          }

          .author-avatar {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background-size: cover;
            background-position: top center;
          }

          .author-meta h4 {
            margin: 0 0 0.25rem 0;
            font-size: 1.1rem;
            color: var(--color-primary-navy);
          }

          .author-meta p {
            margin: 0;
            font-size: var(--fs-small);
            color: var(--color-secondary-grey);
            font-weight: 600;
          }

          .article-meta-list {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .article-meta-list li {
            margin-bottom: 1.5rem;
          }

          .meta-label {
            display: block;
            font-size: 0.75rem;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: var(--color-secondary-grey);
            margin-bottom: 0.25rem;
          }

          .meta-value {
            font-size: 0.95rem;
            font-weight: 600;
            color: var(--color-primary-dark);
          }

          .back-link {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            font-weight: 700;
            color: var(--color-primary-navy);
            text-decoration: none;
            margin-top: 3rem;
            font-size: var(--fs-small);
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: color 0.3s ease;
          }

          .back-link:hover {
            color: var(--color-secondary-grey);
          }

          /* --- ARTICLE BODY TYPOGRAPHY --- */
          .article-body h2 {
            font-size: 2.2rem;
            color: var(--color-primary-navy);
            margin: 4rem 0 1.5rem 0;
            letter-spacing: -0.5px;
          }

          .article-body h3 {
            font-size: var(--fs-h3);
            color: var(--color-primary-dark);
            margin: 2.5rem 0 1rem 0;
          }

          .article-body p {
            font-size: 1.1rem;
            line-height: 1.8;
            color: var(--color-primary-dark);
            margin-bottom: 1.5rem;
          }

          /* FIXED: Force remove default HTML bullets and strictly use the custom '-' */
          .article-body ul {
            margin: 2rem 0 2rem 1rem;
            padding: 0;
            list-style: none; 
          }

          .article-body li {
            font-size: 1.1rem;
            line-height: 1.8;
            color: var(--color-primary-dark);
            margin-bottom: 0.75rem;
            position: relative;
            padding-left: 2rem; 
          }

          .article-body li::before {
            content: '—';
            position: absolute;
            left: 0;
            top: 0;
            color: var(--color-primary-navy);
            font-weight: bold;
          }

          .article-blockquote {
            margin: 4rem 0;
            padding: 3rem 4rem;
            background-color: var(--color-secondary-light);
            border-left: 4px solid var(--color-primary-navy);
            border-radius: 0 16px 16px 0;
          }

          .article-blockquote p {
            font-family: var(--font-heading);
            font-size: 1.6rem;
            line-height: 1.4;
            color: var(--color-primary-navy);
            margin: 0;
            font-weight: 600;
            letter-spacing: -0.5px;
          }

          /* --- RESPONSIVE --- */
          @media (max-width: 992px) {
            .article-layout-grid {
              grid-template-columns: 1fr;
              gap: 4rem;
            }
            .article-sidebar {
              position: static;
              display: flex;
              flex-wrap: wrap;
              gap: 2rem;
              align-items: center;
              border-bottom: 1px solid rgba(0,0,0,0.1);
              padding-bottom: 2rem;
            }
            .author-block { border-bottom: none; padding-bottom: 0; margin-bottom: 0; }
            .article-hero-image { height: 40vh; margin-top: -3rem; margin-bottom: 3rem; }
            .article-blockquote { padding: 2rem; }
            .article-blockquote p { font-size: 1.3rem; }
          }

          @media (max-width: 768px) {
            /* Hero adjustments to prevent overlap with header */
            .news-detail-hero { padding: 13rem 1.5rem 5rem 1.5rem !important; }
            .news-detail-hero h1 { font-size: 2.5rem !important; line-height: 1.2 !important; }
            .news-detail-hero p { font-size: 1.1rem !important; }

            /* Enforce global container boundaries */
            .container { padding-left: 1.5rem !important; padding-right: 1.5rem !important; }

            /* Fix CTA layout overlapping screen edges */
            .sleek-cta-banner-override { flex-direction: column !important; text-align: center !important; padding: 3rem 1.5rem !important; }
            .sleek-cta-banner-override > div { min-width: 100% !important; text-align: center !important; }
            .sleek-cta-banner-override p { margin: 0 auto !important; }
          }
        `}
      </style>

      {/* 1. DARK HERO SECTION */}
      <section className="news-hero news-detail-hero" style={{ textAlign: 'left', alignItems: 'flex-start', paddingBottom: '10rem' }}>
        <div className="container" style={{ margin: '0 auto', maxWidth: '1200px', position: 'relative', zIndex: 2 }}>
          <span style={{ 
            fontSize: 'var(--fs-small)', 
            fontWeight: '800', 
            color: 'var(--color-secondary-grey)', 
            textTransform: 'uppercase', 
            letterSpacing: '2px', 
            marginBottom: '1.5rem', 
            display: 'inline-block' 
          }}>
            {article.category}
          </span>
          <h1 style={{ 
            fontSize: '3.5rem', 
            lineHeight: '1.1', 
            color: 'var(--color-white)', 
            letterSpacing: '-1.5px', 
            marginBottom: '1.5rem', 
            maxWidth: '1000px',
            textAlign: 'left'
          }}>
            {article.title}
          </h1>
          <p style={{ 
            fontSize: 'var(--fs-h3)', 
            lineHeight: '1.6', 
            color: 'var(--color-secondary-light)', 
            maxWidth: '800px', 
            marginBottom: '0',
            textAlign: 'left'
          }}>
            {article.lead}
          </p>
        </div>
      </section>

      {/* 2. OVERLAPPING HERO IMAGE (Dynamic) */}
      <section className="container">
        <div 
          className="article-hero-image" 
          style={{ backgroundImage: `url("${article.image}")` }}
          aria-label={article.title}
        ></div>
      </section>

      {/* 3. ARTICLE CONTENT GRID */}
      <section className="container">
        <div className="article-layout-grid">
          
          {/* Sidebar / Meta */}
          <aside className="article-sidebar">
            <div className="author-block">
              {/* Dynamic Author Avatar Fallback to lauraSteele if missing */}
              <div className="author-avatar" style={{ backgroundImage: `url(${lauraSteele})` }}></div>
              <div className="author-meta">
                <h4>{article.author}</h4>
                <p>{article.authorRole}</p>
              </div>
            </div>
            
            <ul className="article-meta-list">
              <li>
                <span className="meta-label">Published</span>
                <span className="meta-value">{article.date}</span>
              </li>
              <li>
                <span className="meta-label">Reading Time</span>
                <span className="meta-value">{article.readTime}</span>
              </li>
            </ul>

            <Link to="/news" className="back-link">
              ‹ Back to Insights
            </Link>
          </aside>

          {/* Main Article Body (Dynamic) */}
          <article className="article-body">
            {article.content}
          </article>

        </div>
      </section>

      {/* 4. SLEEK FLOATING CTA BANNER */}
      <section style={{ backgroundColor: 'var(--color-light)', paddingTop: '4rem', paddingBottom: '8rem' }}>
        <div className="container">
          <div 
            className="sleek-cta-banner-override"
            style={{ 
            backgroundImage: `linear-gradient(to right, rgba(23, 44, 79, 0.95), rgba(23, 44, 79, 0.8)), url(${bannerImg})`,
            borderRadius: '16px',
            padding: '3rem 4rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            gap: '2rem',
            flexWrap: 'wrap',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', top: '-20px', right: '2%', fontSize: '12rem', color: 'var(--color-white)', opacity: '0.03', pointerEvents: 'none', fontWeight: '900', letterSpacing: '-15px' }}>»»</div>

            <div style={{ textAlign: 'left', flex: '1', position: 'relative', zIndex: 2, minWidth: '300px' }}>
              <h2 style={{ color: 'var(--color-white)', marginBottom: '0.5rem', fontSize: 'var(--fs-h2)', letterSpacing: '-0.5px' }}>Secure Your Compliance Plan Today</h2>
              <p style={{ color: 'var(--color-secondary-light)', fontSize: 'var(--fs-body)', margin: 0, maxWidth: '600px', lineHeight: 'var(--lh-body)' }}>
                Speak to our executive team to discover how our intelligence-led approach can protect your corporate operations.
              </p>
            </div>
            
            <div style={{ position: 'relative', zIndex: 2, flexShrink: 0 }}>
              <Link to="/contact" className="btn btn-pill" style={{ 
                backgroundColor: 'var(--color-white)', 
                color: 'var(--color-primary-navy)', 
                fontWeight: '700', 
                padding: '1rem 2.5rem', 
                fontSize: 'var(--fs-body)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
              }}>
                Contact Operations
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default NewsDetail;